import { useAuth } from "@elit-hair/elit-tools";
import { Badge, Card, ErrorState, Icon, Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarOff, TbClock } from "react-icons/tb";
import z from "zod";

import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { PERMISSIONS } from "@/constants/permissions";
import { useAppointments } from "@/hooks/queries/appointments.queries";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { useClock } from "@/providers/clock-provider";
import { useCall } from "@/providers/hooks/use-call";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { AppointmentBrandSwitchDialog } from "./components/appointment-brand-switch-dialog";
import { AppointmentDetailView } from "./components/appointment-detail-view";
import { AppointmentsTimeline } from "./components/appointments-timeline";
import { useAppointmentEventBrand } from "./hooks/use-appointment-event-brand";
import { canReadAllCalendarBookings, canSelectAppointmentMember } from "./utils/appointment-booking-access.utils";
import { getDefaultSelectedAppointmentId } from "./utils/appointment-selection.utils";

const AppointmentsSearchSchema = z.object({
    userId: z.string().optional(),
    date: z.string().optional(),
    appointmentId: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.APPOINTMENTS)({
    component: Appointments,
    validateSearch: searchValidator(AppointmentsSearchSchema),
    staticData: {
        config: {
            title: "appointments",
            layout: {
                scroll: false,
            },
        },
        permissions: [PERMISSIONS.CALL_CALENDAR_READ],
    },
});

function Appointments() {
    const { t, i18n } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const { userId: selectedUserId, date: dateParam, appointmentId: selectedAppointmentId } = search;
    const { userData } = useAuth();
    const { permissionCodes } = useDashboardRole();
    const { currentTime } = useClock();

    const selectedDate = useMemo(() => {
        if (!dateParam) {
            return DateTime.now().startOf("day");
        }

        const parsed = DateTime.fromISO(dateParam);
        return parsed.isValid ? parsed.startOf("day") : DateTime.now().startOf("day");
    }, [dateParam]);

    const canReadAllBookings = canReadAllCalendarBookings(permissionCodes);
    const canSelectMember = canSelectAppointmentMember(permissionCodes);

    const effectiveUserId = canSelectMember ? (selectedUserId ?? userData?.id) : userData?.id;

    const updateSearch = useCallback(
        (updates: { userId?: string; date?: string; appointmentId?: string | null }) => {
            navigate({
                to: Route.to,
                search: (old) => {
                    const { appointmentId: nextAppointmentId, ...restUpdates } = updates;

                    if (nextAppointmentId === null) {
                        const { appointmentId, ...oldWithoutAppointmentId } = old;
                        void appointmentId;
                        return { ...oldWithoutAppointmentId, ...restUpdates };
                    }

                    if (nextAppointmentId !== undefined) {
                        return { ...old, ...restUpdates, appointmentId: nextAppointmentId };
                    }

                    return { ...old, ...restUpdates };
                },
                replace: true,
                resetScroll: false,
            });
        },
        [navigate]
    );

    useEffect(() => {
        if (!userData?.id) return;

        if (!canSelectMember) {
            if (selectedUserId === userData.id) return;
            updateSearch({ userId: userData.id });
            return;
        }

        if (selectedUserId) return;
        updateSearch({ userId: userData.id });
    }, [canSelectMember, selectedUserId, userData?.id, updateSearch]);

    useEffect(() => {
        if (dateParam) return;

        updateSearch({ date: DateTime.now().toFormat("yyyy-MM-dd") });
    }, [dateParam, updateSearch]);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch: refetchMyAppointments,
    } = useAppointments({
        hostId: effectiveUserId,
        enabled: Boolean(effectiveUserId),
        dateFrom: selectedDate.toFormat("yyyy-MM-dd"),
        dateTo: selectedDate.toFormat("yyyy-MM-dd"),
    });

    const dayAppointments = data ?? [];
    const appointments = useMemo(() => dayAppointments.filter((appointment) => appointment.hostId), [dayAppointments]);
    const unassignedAppointments = useMemo(
        () => dayAppointments.filter((appointment) => !appointment.hostId),
        [dayAppointments]
    );

    useEffect(() => {
        const selectableAppointments = [...appointments, ...unassignedAppointments];

        if (!selectableAppointments.length) {
            if (selectedAppointmentId) {
                updateSearch({ appointmentId: null });
            }
            return;
        }

        const stillExists = selectableAppointments.some((appointment) => appointment.id === selectedAppointmentId);
        if (stillExists) {
            return;
        }

        // Prefer own appointments for default selection; only fall back to unassigned if none.
        const defaultId =
            getDefaultSelectedAppointmentId(appointments, selectedDate, currentTime) ??
            (unassignedAppointments.length
                ? getDefaultSelectedAppointmentId(unassignedAppointments, selectedDate, currentTime)
                : null);

        updateSearch({ appointmentId: defaultId });
    }, [appointments, unassignedAppointments, selectedAppointmentId, selectedDate, currentTime, updateSearch]);

    const selectedAppointment = useMemo(() => {
        const fromOwn = appointments.find((appointment) => appointment.id === selectedAppointmentId);
        if (fromOwn) return fromOwn;
        return unassignedAppointments.find((appointment) => appointment.id === selectedAppointmentId) ?? null;
    }, [appointments, unassignedAppointments, selectedAppointmentId]);

    const isSelectedDateToday = selectedDate.hasSame(DateTime.now(), "day");
    const formattedSelectedDate = selectedDate.setLocale(i18n.language).toFormat("EEEE, d. MMMM yyyy");

    // The details load the CRM data of the selected brand: ask to switch when the event is another brand.
    const { selectedBrand, setSelectedBrand, isCallActive } = useCall();
    const appointmentBrand = useAppointmentEventBrand(selectedAppointment);
    const [brandCheckedAppointmentId, setBrandCheckedAppointmentId] = useState<string | null>(null);
    const showBrandSwitch =
        Boolean(selectedAppointment && appointmentBrand && appointmentBrand !== selectedBrand) &&
        brandCheckedAppointmentId !== selectedAppointment?.id &&
        !isCallActive();

    const handleMemberChange = useCallback(
        (memberId: string | null) => {
            if (!canSelectMember) return;
            updateSearch({ userId: memberId ?? userData?.id, appointmentId: null });
        },
        [canSelectMember, updateSearch, userData?.id]
    );

    const handleDateChange = useCallback(
        (nextDate: DateTime) => {
            updateSearch({
                date: nextDate.toFormat("yyyy-MM-dd"),
                appointmentId: null,
            });
        },
        [updateSearch]
    );

    const handleRefresh = useCallback(() => {
        void refetchMyAppointments();
    }, [refetchMyAppointments]);

    const memberSelector = canSelectMember ? (
        <TeamMemberSelector
            selectedMemberId={effectiveUserId}
            onMemberChange={handleMemberChange}
            currentUserId={userData?.id}
            includeUsersWithNoTeam={canReadAllBookings}
            labels={{ resetSelection: "my_appointments" }}
        />
    ) : undefined;

    const hasAnyVisibleAppointments = appointments.length > 0 || unassignedAppointments.length > 0;

    return (
        <div className="flex h-full min-h-0 flex-col">
            <AppointmentsTimeline
                appointments={appointments}
                unassignedAppointments={unassignedAppointments}
                selectedAppointmentId={selectedAppointmentId ?? null}
                onSelect={(appointmentId) => updateSearch({ appointmentId })}
                selectedDate={selectedDate}
                onPrevDay={() => handleDateChange(selectedDate.minus({ days: 1 }))}
                onToday={() => handleDateChange(DateTime.now())}
                onNextDay={() => handleDateChange(selectedDate.plus({ days: 1 }))}
                onRefresh={handleRefresh}
                isFetching={isFetching}
                isAppointmentsReady={!isLoading}
                memberSelector={memberSelector}
            >
                {isLoading && !appointments.length ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Spinner size="lg" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title={t("failed_to_load_appointments")}
                        message={error?.message || t("appointments_load_error")}
                        onRetry={handleRefresh}
                        retryLabel={t("try_again")}
                    />
                ) : !hasAnyVisibleAppointments ? (
                    <Card className="flex flex-1 flex-col items-center justify-center gap-4 border border-border/60 bg-card/70 p-8 text-center shadow-sm">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/30">
                            <Icon icon={TbCalendarOff} />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-foreground">
                                {isSelectedDateToday
                                    ? t("no_upcoming_appointments_today")
                                    : t("no_appointments_on_date", { date: formattedSelectedDate })}
                            </h3>
                            <p className="max-w-sm text-sm text-muted-foreground">
                                {isSelectedDateToday
                                    ? t("you_don_t_have_any_appointments_scheduled_for_today")
                                    : t("no_appointments_scheduled_on_date", { date: formattedSelectedDate })}
                            </p>
                        </div>
                        {isSelectedDateToday ? (
                            <Badge variant="secondary" className="px-3 py-1 text-xs">
                                <Icon icon={TbClock} size={12} className="mr-1" />
                                {t("all_caught_up")}
                            </Badge>
                        ) : null}
                    </Card>
                ) : (
                    selectedAppointment && (
                        <AppointmentDetailView key={selectedAppointment.id} appointment={selectedAppointment} />
                    )
                )}
            </AppointmentsTimeline>

            <AppointmentBrandSwitchDialog
                appointmentBrand={showBrandSwitch ? (appointmentBrand ?? null) : null}
                currentBrand={selectedBrand ?? null}
                onSwitch={(brandName) => {
                    setSelectedBrand(brandName);
                    setBrandCheckedAppointmentId(selectedAppointment?.id ?? null);
                }}
                onKeepCurrent={() => setBrandCheckedAppointmentId(selectedAppointment?.id ?? null)}
            />
        </div>
    );
}
